import { useCallback } from 'react';
import { useNavigate } from '@remix-run/react';
import { CalendarIcon, PhoneIcon } from 'lucide-react';
import { DetailsPageIsland } from '@rocco/ui/floating-island/components/detailspage-island/DetailsPageIsland';
import { ReactComponent as EnquiryIcon } from '@rocco/icons/svg/message.svg';
import { useRootData } from '~/modules/root';
import { Button } from '@rocco/components/button';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import type { ProjectPageLoader } from '../../type';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
import { usePhoneTracking } from '~/modules/tracking/hook/usePhoneTracking';

interface ProjectIslandProps {
    project: ProjectPageLoader['project'];
    // Note: Currently not being used, maybe will be used in the future?
    primaryCTA: ProjectPageLoader['primaryCTA'];
}

export const ProjectIsland = ({ project }: ProjectIslandProps) => {
    const { ID: projectId, Professional } = project;
    const navigate = useNavigate();
    const { appDisplayMode } = useRootData();

    const { openEnquiryModal } = useEnquiryModal({
        title: 'Request a Consultation',
        professional: Professional,
        itemType: 'project',
        siteTreeID: projectId,
        source: 'ProjectIsland',
        ctaLabel: 'Request a Consultation',
        relatedItem: {
            id: projectId,
            type: 'project',
            title: project.Title,
            category: project.categoryPath,
        },
    });

    const {
        enabled,
        phone: phoneWithTracking,
        handlePhoneClick,
        phoneLabel,
    } = usePhoneTracking();

    const showPhoneTrackingCTA = enabled && !!phoneWithTracking;

    const onEnquiryClick = (label: string) => {
        openEnquiryModal({ title: label, ctaLabel: label });
    };

    const {
        CompanyLogo,
        LogoBackgroundColor,
        Link: professionalLink,
        Title: professionalTitle,
        Categories,
    } = Professional;

    const handleIslandClick = useCallback(() => {
        if (professionalLink) {
            navigate(professionalLink);
        }
    }, [professionalLink, navigate]);

    if (appDisplayMode === DisplayMode.Mobile && !showPhoneTrackingCTA) {
        return null;
    }

    return (
        <DetailsPageIsland
            key={`project-island-${projectId}`}
            displayMode={appDisplayMode}
            scrollThreshold={0}
            image={{
                image: {
                    src: CompanyLogo,
                    alt: professionalTitle,
                },
                background: LogoBackgroundColor,
                link: professionalLink,
                size: 'professionalLogo',
            }}
            title={professionalTitle}
            description={Categories[0]?.PrettyTitle ?? ''}
            onClick={handleIslandClick}
            primaryAction={
                <ProjectIslandEnquiryButton
                    label={'Request a Consultation'}
                    onClick={() => onEnquiryClick('Request a Consultation')}
                    displayMode={appDisplayMode}
                    icon={
                        <CalendarIcon
                            aria-label="Request a Consultation"
                            className="w-4"
                        />
                    }
                    type="primary"
                />
            }
            secondaryAction={
                showPhoneTrackingCTA ? (
                    <ProjectIslandCallButton
                        label={phoneLabel ?? 'Call Us'}
                        onClick={handlePhoneClick}
                        displayMode={appDisplayMode}
                    />
                ) : (
                    <ProjectIslandEnquiryButton
                        label="Send an Enquiry"
                        onClick={() => onEnquiryClick('Send an Enquiry')}
                        displayMode={appDisplayMode}
                        icon={
                            <EnquiryIcon
                                aria-label="Send an Enquiry"
                                className="w-4 fill-black"
                            />
                        }
                        type="secondary"
                    />
                )
            }
        />
    );
};

const ProjectIslandEnquiryButton = ({
    label,
    onClick,
    displayMode,
    icon,
    type,
}: {
    label: string;
    onClick: () => void;
    displayMode: DisplayMode;
    icon: React.ReactNode;
    type: 'primary' | 'secondary';
}) => {
    const compact = displayMode === DisplayMode.Mobile;

    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color={type === 'primary' ? 'primary' : 'secondary70'}
            onClick={onClick}
        >
            <span className="flex items-center gap-2">
                {compact ? null : label}
                {icon}
            </span>
        </Button>
    );
};

const ProjectIslandCallButton = ({
    label,
    onClick,
    displayMode,
}: {
    label: string;
    onClick: () => void;
    displayMode: DisplayMode;
}) => {
    const compact = displayMode === DisplayMode.Mobile;

    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color="white"
            onClick={onClick}
        >
            <span className="flex items-center gap-2">
                {compact ? null : label}
                <PhoneIcon aria-label={label} className="w-4 fill-black" />
            </span>
        </Button>
    );
};
